import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  serverTimestamp,
  onSnapshot,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch
} from 'firebase/firestore';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { currentUser, updateCurrentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Impersonation state
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  
  // Get current effective user (either impersonated or real)
  const effectiveUser = impersonatedUser || currentUser;
  
  // State for all data
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]); // חדש - הערות
  const [suggestions, setSuggestions] = useState([]);
  const [weeklyPlans, setWeeklyPlans] = useState([]); // חדש - תכנונים שבועיים
  const [dailyPlans, setDailyPlans] = useState([]); // חדש - תכנונים יומיים
  const [meetings, setMeetings] = useState([]); // חדש - ישיבות הנהלה

  useEffect(() => {
    // Wait until we have a complete user object with role
    if (!effectiveUser?.id || !effectiveUser?.role) {
      return;
    }

    const projectsQueryRef = query(
      collection(db, COLLECTIONS.PROJECTS),
      orderBy('createdAt', 'desc')
    );

    const tasksQueryRef = query(
      collection(db, COLLECTIONS.TASKS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeProjects = onSnapshot(projectsQueryRef, (snapshot) => {
      const projectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(projectsData);
    }, (error) => {
      setProjects([]);
    });

    const unsubscribeTasks = onSnapshot(tasksQueryRef, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTasks(tasksData);
    }, (error) => {
      setTasks([]);
    });

    // Load users
    const usersQueryRef = collection(db, COLLECTIONS.USERS);
    const unsubscribeUsers = onSnapshot(usersQueryRef, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
    }, (error) => {
      setUsers([]);
    });

    // Load departments
    const departmentsQueryRef = collection(db, COLLECTIONS.DEPARTMENTS);
    const unsubscribeDepartments = onSnapshot(departmentsQueryRef, (snapshot) => {
      const departmentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDepartments(departmentsData);
      
      // Initialize management department cache when departments are loaded
      const managementDept = departmentsData.find(dept => 
        dept.name === 'מחלקת הנהלה' || dept.name === 'Management Department'
      );
      if (managementDept) {
        managementDepartmentId = managementDept.id;
        console.log('Initialized management department cache:', managementDepartmentId);
      }
    }, (error) => {
      setDepartments([]);
    });

    // Load teams
    const teamsQueryRef = collection(db, COLLECTIONS.TEAMS);
    const unsubscribeTeams = onSnapshot(teamsQueryRef, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTeams(teamsData);
    }, (error) => {
      setTeams([]);
    });

    // Load comments
    const commentsQueryRef = query(
      collection(db, COLLECTIONS.COMMENTS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeComments = onSnapshot(commentsQueryRef, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setComments(commentsData);
    }, (error) => {
      console.error('Error loading comments:', error);
      setComments([]);
    });

    // Load suggestions
    const suggestionsQueryRef = query(
      collection(db, 'suggestions'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeSuggestions = onSnapshot(suggestionsQueryRef, (snapshot) => {
      const suggestionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSuggestions(suggestionsData);
    }, (error) => {
      console.error('Error loading suggestions:', error);
      setSuggestions([]);
    });

    // Load weekly plans
    const weeklyPlansQueryRef = query(
      collection(db, 'weeklyPlans'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeWeeklyPlans = onSnapshot(weeklyPlansQueryRef, (snapshot) => {
      const weeklyPlansData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWeeklyPlans(weeklyPlansData);
    }, (error) => {
      console.error('Error loading weekly plans:', error);
      setWeeklyPlans([]);
    });

    // Load daily plans
    const dailyPlansQueryRef = query(
      collection(db, 'dailyPlans'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeDailyPlans = onSnapshot(dailyPlansQueryRef, (snapshot) => {
      const dailyPlansData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDailyPlans(dailyPlansData);
    }, (error) => {
      console.error('Error loading daily plans:', error);
      setDailyPlans([]);
    });

    // Load meetings
    const meetingsQueryRef = query(
      collection(db, COLLECTIONS.MEETINGS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeMeetings = onSnapshot(meetingsQueryRef, (snapshot) => {
      const meetingsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMeetings(meetingsData);
    }, (error) => {
      console.error('Error loading meetings:', error);
      setMeetings([]);
    });

    // Load notifications for current user
    const notificationsQueryRef = query(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      where('userId', '==', effectiveUser.id),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeNotifications = onSnapshot(notificationsQueryRef, (snapshot) => {
      const notificationsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNotifications(notificationsData);
    }, (error) => {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    });

    setLoading(false);

    return () => {
      if (typeof unsubscribeProjects === 'function') unsubscribeProjects();
      if (typeof unsubscribeTasks === 'function') unsubscribeTasks();
      if (typeof unsubscribeUsers === 'function') unsubscribeUsers();
      if (typeof unsubscribeDepartments === 'function') unsubscribeDepartments();
      if (typeof unsubscribeTeams === 'function') unsubscribeTeams();
      if (typeof unsubscribeComments === 'function') unsubscribeComments();
      if (typeof unsubscribeSuggestions === 'function') unsubscribeSuggestions();
      if (typeof unsubscribeWeeklyPlans === 'function') unsubscribeWeeklyPlans();
      if (typeof unsubscribeDailyPlans === 'function') unsubscribeDailyPlans();
      if (typeof unsubscribeMeetings === 'function') unsubscribeMeetings();
      if (typeof unsubscribeNotifications === 'function') unsubscribeNotifications();
    };
  }, [effectiveUser?.id]);

  // Remove demo initialization: departments and users should be loaded from Firestore elsewhere
  const initializeDemoData = () => {};

  // Task operations
  const createTask = async (taskData) => {
    try {
      // Determine if task requires approval
      const requiresApproval = taskData.scope === 'managerial' || 
                              (taskData.assigneeId !== effectiveUser.id && taskData.scope !== 'personal');
      
      // Set initial status based on approval requirement
      let initialStatus = taskData.status || 'ממתין לעבודה';
      if (requiresApproval && !taskData.status) {
        initialStatus = 'ממתין לעבודה';
      }
      
      const newTask = {
        ...taskData,
        creatorId: effectiveUser.id,
        createdAt: serverTimestamp(),
        acknowledged: false,
        requiresApproval: requiresApproval,
        status: initialStatus,
        approvedBy: null,
        approvedAt: null
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.TASKS), newTask);

      // Auto-add assignees to project team if projectId exists
      try {
        if (newTask.projectId) {
          const project = projects.find(p => p.id === newTask.projectId);
          const currentTeam = Array.isArray(project?.teamMembers) ? project.teamMembers : [];
          const assignees = Array.isArray(newTask.assigneeIds) && newTask.assigneeIds.length
            ? newTask.assigneeIds
            : (newTask.assigneeId ? [newTask.assigneeId] : []);
          const updatedTeam = Array.from(new Set([...currentTeam, ...assignees]));
          if (project && updatedTeam.length !== currentTeam.length) {
            await updateDoc(doc(db, COLLECTIONS.PROJECTS, project.id), {
              teamMembers: updatedTeam,
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (teamErr) {
        // Could not auto-add assignees to project team
      }

      return docRef.id;
    } catch (error) {
      // Fallback to local state for demo
      const localTask = {
        id: `task_${Date.now()}`,
        ...taskData,
        creatorId: effectiveUser.id,
        createdAt: new Date(),
        acknowledged: false,
        status: taskData.status || 'במסלול'
      };
      
      setTasks(prev => [localTask, ...prev]);
      
      return localTask.id;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      // Try to update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Auto-add assignees to project team if projectId exists
      try {
        const taskAfter = tasks.find(t => t.id === taskId) || {};
        const projectId = updates.projectId || taskAfter.projectId;
        if (projectId) {
          const project = projects.find(p => p.id === projectId);
          const currentTeam = Array.isArray(project?.teamMembers) ? project.teamMembers : [];
          const assignees = Array.isArray(updates.assigneeIds) && updates.assigneeIds.length
            ? updates.assigneeIds
            : (updates.assigneeId ? [updates.assigneeId] : (Array.isArray(taskAfter.assigneeIds) ? taskAfter.assigneeIds : (taskAfter.assigneeId ? [taskAfter.assigneeId] : [])));
          const updatedTeam = Array.from(new Set([...currentTeam, ...assignees]));
          if (project && updatedTeam.length !== currentTeam.length) {
            await updateDoc(doc(db, COLLECTIONS.PROJECTS, project.id), {
              teamMembers: updatedTeam,
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (teamErr) {
        // Could not auto-add assignees to project team on update
      }
      
    } catch (error) {
      // Fallback to local state update
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { ...task, ...updates, updatedAt: new Date() }
            : task
        )
      );
    }
  };

  const acknowledgeTask = async (taskId) => {
    try {
      const acknowledgeData = {
        acknowledged: true,
        acknowledgedAt: serverTimestamp()
      };

      // Try to update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), acknowledgeData);
      
    } catch (error) {
      // Fallback to local state update
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { ...task, acknowledged: true, acknowledgedAt: new Date() }
            : task
        )
      );
    }
  };

  // New function for approving tasks
  const approveTask = async (taskId) => {
    try {
      const approvalData = {
        status: 'במסלול',
        approvedBy: effectiveUser.id,
        approvedAt: serverTimestamp(),
        acknowledged: true,
        acknowledgedAt: serverTimestamp()
      };

      // Try to update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), approvalData);
      
    } catch (error) {
      // Fallback to local state update
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                status: 'במסלול',
                approvedBy: effectiveUser.id,
                approvedAt: new Date(),
                acknowledged: true,
                acknowledgedAt: new Date()
              }
            : task
        )
      );
    }
  };

  const deleteTask = async (taskId) => {
    try {
      // Soft delete - mark as deleted instead of removing
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), {
        deleted: true,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, deleted: true, deletedAt: new Date(), updatedAt: new Date() }
          : task
      ));
      
    } catch (error) {
      console.error('Error deleting task:', error);
      // Fallback to local state update
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, deleted: true, deletedAt: new Date(), updatedAt: new Date() }
          : task
      ));
    }
  };

  // Restore deleted task
  const restoreTask = async (taskId) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), {
        deleted: false,
        deletedAt: null,
        updatedAt: serverTimestamp()
      });
      
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, deleted: false, deletedAt: null, updatedAt: new Date() }
          : task
      ));
      
    } catch (error) {
      console.error('Error restoring task:', error);
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, deleted: false, deletedAt: null, updatedAt: new Date() }
          : task
      ));
    }
  };

  // Permanently delete task (after 14 days or manual)
  const permanentDeleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.TASKS, taskId));
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (error) {
      console.error('Error permanently deleting task:', error);
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }
  };

  // Project operations
  const createProject = async (projectData) => {
    try {
      const newProject = {
        ...projectData,
        createdBy: effectiveUser.id,
        createdAt: serverTimestamp(),
        status: projectData.status || 'במסלול',
        progress: projectData.progress || 0
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.PROJECTS), newProject);
      
      return docRef.id;
    } catch (error) {
      // Fallback to local state for demo
      const localProject = {
        id: `project_${Date.now()}`,
        ...projectData,
        createdBy: effectiveUser.id,
        createdAt: new Date(),
        status: projectData.status || 'במסלול',
        progress: projectData.progress || 0
      };
      
      setProjects(prev => [localProject, ...prev]);
      
      return localProject.id;
    }
  };

  const updateProject = async (projectId, updates) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.PROJECTS, projectId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Also update local state immediately
      setProjects(prev => 
        prev.map(project => 
          project.id === projectId 
            ? { ...project, ...updates, updatedAt: new Date() }
            : project
        )
      );
      
    } catch (error) {
      // Fallback to local state update only
      setProjects(prev => 
        prev.map(project => 
          project.id === projectId 
            ? { ...project, ...updates, updatedAt: new Date() }
            : project
        )
      );
    }
  };

  const deleteProject = async (projectId) => {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.PROJECTS, projectId));
      
      // Also update local state immediately
      setProjects(prev => prev.filter(project => project.id !== projectId));
      
    } catch (error) {
      // Fallback to local state update only
      setProjects(prev => prev.filter(project => project.id !== projectId));
    }
  };

  // Team operations
  const createTeam = async (teamData) => {
    try {
      const newTeam = {
        ...teamData,
        createdBy: effectiveUser.id,
        createdAt: serverTimestamp(),
        isActive: teamData.isActive ?? true
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.TEAMS), newTeam);
      
      // עדכון המשתמשים עם ה-teamId החדש (תמיכה במספר צוותים ומחלקות)
      const batch = writeBatch(db);
      
      // עדכון מנהל הצוות - הוספה למערכי teamIds ו-departmentIds
      if (teamData.managerId) {
        const manager = users.find(u => u.id === teamData.managerId);
        const currentTeamIds = Array.isArray(manager?.teamIds) ? manager.teamIds : (manager?.teamId ? [manager.teamId] : []);
        const currentDeptIds = Array.isArray(manager?.departmentIds) ? manager.departmentIds : (manager?.departmentId ? [manager.departmentId] : []);
        
        const newTeamIds = [...new Set([...currentTeamIds, docRef.id])];
        const newDeptIds = teamData.departmentId && !currentDeptIds.includes(teamData.departmentId) 
          ? [...currentDeptIds, teamData.departmentId] 
          : currentDeptIds;
        
        const managerRef = doc(db, COLLECTIONS.USERS, teamData.managerId);
        batch.update(managerRef, { 
          teamIds: newTeamIds,
          teamId: newTeamIds[0], // backward compatibility
          departmentIds: newDeptIds,
          departmentId: newDeptIds[0], // backward compatibility
          updatedAt: serverTimestamp()
        });
      }
      
      // עדכון חברי הצוות - הוספה למערכי teamIds ו-departmentIds
      if (teamData.teamMembers && teamData.teamMembers.length > 0) {
        teamData.teamMembers.forEach(userId => {
          const member = users.find(u => u.id === userId);
          const currentTeamIds = Array.isArray(member?.teamIds) ? member.teamIds : (member?.teamId ? [member.teamId] : []);
          const currentDeptIds = Array.isArray(member?.departmentIds) ? member.departmentIds : (member?.departmentId ? [member.departmentId] : []);
          
          const newTeamIds = [...new Set([...currentTeamIds, docRef.id])];
          const newDeptIds = teamData.departmentId && !currentDeptIds.includes(teamData.departmentId) 
            ? [...currentDeptIds, teamData.departmentId] 
            : currentDeptIds;
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamIds: newTeamIds,
            teamId: newTeamIds[0], // backward compatibility
            departmentIds: newDeptIds,
            departmentId: newDeptIds[0], // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
      }
      
      await batch.commit();
      
      // עדכון המצב המקומי
      setUsers(prev => 
        prev.map(user => {
          if (user.id === teamData.managerId) {
            const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
            const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
            const newTeamIds = [...new Set([...currentTeamIds, docRef.id])];
            const newDeptIds = teamData.departmentId && !currentDeptIds.includes(teamData.departmentId) 
              ? [...currentDeptIds, teamData.departmentId] 
              : currentDeptIds;
            return { 
              ...user, 
              teamIds: newTeamIds, 
              teamId: newTeamIds[0],
              departmentIds: newDeptIds, 
              departmentId: newDeptIds[0],
              updatedAt: new Date() 
            };
          }
          if (teamData.teamMembers && teamData.teamMembers.includes(user.id)) {
            const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
            const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
            const newTeamIds = [...new Set([...currentTeamIds, docRef.id])];
            const newDeptIds = teamData.departmentId && !currentDeptIds.includes(teamData.departmentId) 
              ? [...currentDeptIds, teamData.departmentId] 
              : currentDeptIds;
            return { 
              ...user, 
              teamIds: newTeamIds, 
              teamId: newTeamIds[0],
              departmentIds: newDeptIds, 
              departmentId: newDeptIds[0],
              updatedAt: new Date() 
            };
          }
          return user;
        })
      );
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating team:', error);
      
      // Fallback to local state only
      const localTeam = {
        id: `team_${Date.now()}`,
        ...teamData,
        createdBy: effectiveUser.id,
        createdAt: new Date(),
        isActive: teamData.isActive ?? true
      };
      setTeams(prev => [localTeam, ...prev]);
      
      // Update local users state
      setUsers(prev => 
        prev.map(user => {
          if (user.id === teamData.managerId) {
            const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
            const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
            const newTeamIds = [...new Set([...currentTeamIds, localTeam.id])];
            const newDeptIds = teamData.departmentId && !currentDeptIds.includes(teamData.departmentId) 
              ? [...currentDeptIds, teamData.departmentId] 
              : currentDeptIds;
            return { 
              ...user, 
              teamIds: newTeamIds, 
              teamId: newTeamIds[0],
              departmentIds: newDeptIds, 
              departmentId: newDeptIds[0],
              updatedAt: new Date() 
            };
          }
          if (teamData.teamMembers && teamData.teamMembers.includes(user.id)) {
            const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
            const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
            const newTeamIds = [...new Set([...currentTeamIds, localTeam.id])];
            const newDeptIds = teamData.departmentId && !currentDeptIds.includes(teamData.departmentId) 
              ? [...currentDeptIds, teamData.departmentId] 
              : currentDeptIds;
            return { 
              ...user, 
              teamIds: newTeamIds, 
              teamId: newTeamIds[0],
              departmentIds: newDeptIds, 
              departmentId: newDeptIds[0],
              updatedAt: new Date() 
            };
          }
          return user;
        })
      );
      
      return localTeam.id;
    }
  };

  const updateTeam = async (teamId, updates) => {
    try {
      // Get current team to compare teamMembers
      const currentTeam = teams.find(t => t.id === teamId);
      const oldTeamMembers = currentTeam?.teamMembers || [];
      const newTeamMembers = updates.teamMembers || [];
      
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TEAMS, teamId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Handle teamMembers changes (תמיכה במספר צוותים ומחלקות)
      if (updates.teamMembers !== undefined) {
        const batch = writeBatch(db);
        
        // Remove teamId from users no longer in teamMembers
        const removedUsers = oldTeamMembers.filter(id => !newTeamMembers.includes(id));
        removedUsers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentTeamIds = Array.isArray(user?.teamIds) ? user.teamIds : (user?.teamId ? [user.teamId] : []);
          const newTeamIds = currentTeamIds.filter(id => id !== teamId);
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamIds: newTeamIds,
            teamId: newTeamIds.length > 0 ? newTeamIds[0] : null, // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        // Add teamId to new teamMembers
        const addedUsers = newTeamMembers.filter(id => !oldTeamMembers.includes(id));
        addedUsers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentTeamIds = Array.isArray(user?.teamIds) ? user.teamIds : (user?.teamId ? [user.teamId] : []);
          const currentDeptIds = Array.isArray(user?.departmentIds) ? user.departmentIds : (user?.departmentId ? [user.departmentId] : []);
          
          const newTeamIds = [...new Set([...currentTeamIds, teamId])];
          const newDeptIds = currentTeam.departmentId && !currentDeptIds.includes(currentTeam.departmentId) 
            ? [...currentDeptIds, currentTeam.departmentId] 
            : currentDeptIds;
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamIds: newTeamIds,
            teamId: newTeamIds[0], // backward compatibility
            departmentIds: newDeptIds,
            departmentId: newDeptIds[0], // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        if (removedUsers.length > 0 || addedUsers.length > 0) {
          await batch.commit();
          
          // Update local users state
          setUsers(prev => 
            prev.map(user => {
              if (removedUsers.includes(user.id)) {
                const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
                const newTeamIds = currentTeamIds.filter(id => id !== teamId);
                return { 
                  ...user, 
                  teamIds: newTeamIds, 
                  teamId: newTeamIds.length > 0 ? newTeamIds[0] : null,
                  updatedAt: new Date() 
                };
              }
              if (addedUsers.includes(user.id)) {
                const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
                const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
                const newTeamIds = [...new Set([...currentTeamIds, teamId])];
                const newDeptIds = currentTeam.departmentId && !currentDeptIds.includes(currentTeam.departmentId) 
                  ? [...currentDeptIds, currentTeam.departmentId] 
                  : currentDeptIds;
                return { 
                  ...user, 
                  teamIds: newTeamIds, 
                  teamId: newTeamIds[0],
                  departmentIds: newDeptIds, 
                  departmentId: newDeptIds[0],
                  updatedAt: new Date() 
                };
              }
              return user;
            })
          );
        }
      }
      
      // Also update local state immediately
      setTeams(prev => 
        prev.map(team => 
          team.id === teamId 
            ? { ...team, ...updates, updatedAt: new Date() }
            : team
        )
      );
      
    } catch (error) {
      console.error('Error updating team:', error);
      
      // Fallback to local state update only
      setTeams(prev => 
        prev.map(team => 
          team.id === teamId 
            ? { ...team, ...updates, updatedAt: new Date() }
            : team
        )
      );
    }
  };

  const deleteTeam = async (teamId) => {
    try {
      // Get current team to find teamMembers
      const currentTeam = teams.find(t => t.id === teamId);
      const teamMembers = currentTeam?.teamMembers || [];
      
      // Remove teamId from all team members (תמיכה במספר צוותים)
      if (teamMembers.length > 0) {
        const batch = writeBatch(db);
        
        teamMembers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentTeamIds = Array.isArray(user?.teamIds) ? user.teamIds : (user?.teamId ? [user.teamId] : []);
          const newTeamIds = currentTeamIds.filter(id => id !== teamId);
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamIds: newTeamIds,
            teamId: newTeamIds.length > 0 ? newTeamIds[0] : null, // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        // Update local users state
        setUsers(prev => 
          prev.map(user => {
            if (teamMembers.includes(user.id)) {
              const currentTeamIds = Array.isArray(user.teamIds) ? user.teamIds : (user.teamId ? [user.teamId] : []);
              const newTeamIds = currentTeamIds.filter(id => id !== teamId);
              return { 
                ...user, 
                teamIds: newTeamIds, 
                teamId: newTeamIds.length > 0 ? newTeamIds[0] : null,
                updatedAt: new Date() 
              };
            }
            return user;
          })
        );
      }
      
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.TEAMS, teamId));
      
      // Also update local state immediately
      setTeams(prev => prev.filter(team => team.id !== teamId));
      
    } catch (error) {
      console.error('Error deleting team:', error);
      
      // Fallback to local state update only
      setTeams(prev => prev.filter(team => team.id !== teamId));
    }
  };
  // Suggestion operations
  const createSuggestion = async (suggestionData) => {
    try {
      const newSuggestion = {
        ...suggestionData,
        createdAt: serverTimestamp(),
        status: 'pending'
      };
      
      const docRef = await addDoc(collection(db, 'suggestions'), newSuggestion);
      return docRef.id;
    } catch (error) {
      console.error('Error creating suggestion:', error);
      // Fallback to local state
      const localSuggestion = {
        id: `suggestion_${Date.now()}`,
        ...suggestionData,
        createdAt: new Date()
      };
      setSuggestions(prev => [localSuggestion, ...prev]);
      return localSuggestion.id;
    }
  };

  const updateSuggestion = async (suggestionId, updates) => {
    try {
      await updateDoc(doc(db, 'suggestions', suggestionId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating suggestion:', error);
      setSuggestions(prev => 
        prev.map(s => s.id === suggestionId 
          ? { ...s, ...updates, updatedAt: new Date() }
          : s
        )
      );
    }
  };

  const deleteSuggestion = async (suggestionId) => {
    try {
      await deleteDoc(doc(db, 'suggestions', suggestionId));
    } catch (error) {
      console.error('Error deleting suggestion:', error);
    }
    setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
  };

  // Department operations
  
  // Cache for management department ID to prevent multiple creations
  let managementDepartmentId = null;
  let managementDepartmentPromise = null;

  // Create Management Department automatically (only once)
  const createManagementDepartment = async () => {
    try {
      // If we already have the ID cached, return it immediately
      if (managementDepartmentId) {
        console.log('Using cached management department ID:', managementDepartmentId);
        return managementDepartmentId;
      }
      
      // If there's already a creation in progress, wait for it
      if (managementDepartmentPromise) {
        console.log('Management department creation already in progress, waiting...');
        return await managementDepartmentPromise;
      }
      
      // Check if management department already exists in current departments
      const existingManagementDept = departments.find(dept => 
        dept.name === 'מחלקת הנהלה' || dept.name === 'Management Department'
      );
      
      if (existingManagementDept) {
        console.log('Management department already exists:', existingManagementDept.id);
        managementDepartmentId = existingManagementDept.id;
        return existingManagementDept.id;
      }
      
      // Create a promise for the creation process
      managementDepartmentPromise = (async () => {
        console.log('Creating management department...');
        
        // Create management department directly in Firestore
        const managementDepartmentData = {
          name: 'מחלקת הנהלה',
          description: 'מחלקת ההנהלה של החברה - מכילה את כל המנהלים וההנהלה הבכירה',
          managerIds: [],
          teamMembers: [],
          isActive: true,
          isSystemCreated: true, // Flag to identify system-created department
          createdBy: currentUser?.id || 'system',
          createdAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, COLLECTIONS.DEPARTMENTS), managementDepartmentData);
        
        // Update local state
        setDepartments(prev => [...prev, { ...managementDepartmentData, id: docRef.id }]);
        
        // Cache the ID
        managementDepartmentId = docRef.id;
        
        console.log('Management department created with ID:', docRef.id);
        return docRef.id;
      })();
      
      const result = await managementDepartmentPromise;
      
      // Clear the promise after completion
      managementDepartmentPromise = null;
      
      return result;
    } catch (error) {
      console.error('Error creating management department:', error);
      // Clear the promise on error
      managementDepartmentPromise = null;
      return null;
    }
  };
  
  const createDepartment = async (departmentData) => {
    try {
      const newDepartment = {
        ...departmentData,
        createdBy: effectiveUser.id,
        createdAt: serverTimestamp(),
        isActive: departmentData.isActive ?? true
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.DEPARTMENTS), newDepartment);
      
      // עדכון המשתמשים עם ה-departmentId החדש (תמיכה במספר מחלקות)
      if (departmentData.teamMembers && departmentData.teamMembers.length > 0) {
        const batch = writeBatch(db);
        
        departmentData.teamMembers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentDeptIds = Array.isArray(user?.departmentIds) ? user.departmentIds : (user?.departmentId ? [user.departmentId] : []);
          const newDeptIds = [...new Set([...currentDeptIds, docRef.id])];
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentIds: newDeptIds,
            departmentId: newDeptIds[0], // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        // עדכון המצב המקומי
        setUsers(prev => 
          prev.map(user => {
            if (departmentData.teamMembers.includes(user.id)) {
              const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
              const newDeptIds = [...new Set([...currentDeptIds, docRef.id])];
              return { 
                ...user, 
                departmentIds: newDeptIds, 
                departmentId: newDeptIds[0],
                updatedAt: new Date() 
              };
            }
            return user;
          })
        );
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating department:', error);
      
      // Fallback to local state only
      const localDept = {
        id: `dept_${Date.now()}`,
        ...departmentData,
        createdBy: effectiveUser.id,
        createdAt: new Date(),
        isActive: departmentData.isActive ?? true
      };
      setDepartments(prev => [localDept, ...prev]);
      
      // Update local users state
      if (departmentData.teamMembers && departmentData.teamMembers.length > 0) {
        setUsers(prev => 
          prev.map(user => {
            if (departmentData.teamMembers.includes(user.id)) {
              const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
              const newDeptIds = [...new Set([...currentDeptIds, localDept.id])];
              return { 
                ...user, 
                departmentIds: newDeptIds, 
                departmentId: newDeptIds[0],
                updatedAt: new Date() 
              };
            }
            return user;
          })
        );
      }
      
      return localDept.id;
    }
  };

  const updateDepartment = async (departmentId, updates) => {
    try {
      // Get current department to compare teamMembers
      const currentDept = departments.find(d => d.id === departmentId);
      const oldTeamMembers = currentDept?.teamMembers || [];
      const newTeamMembers = updates.teamMembers || [];
      
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.DEPARTMENTS, departmentId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Handle teamMembers changes (תמיכה במספר מחלקות)
      if (updates.teamMembers !== undefined) {
        const batch = writeBatch(db);
        
        // Remove departmentId from users no longer in teamMembers
        const removedUsers = oldTeamMembers.filter(id => !newTeamMembers.includes(id));
        removedUsers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentDeptIds = Array.isArray(user?.departmentIds) ? user.departmentIds : (user?.departmentId ? [user.departmentId] : []);
          const newDeptIds = currentDeptIds.filter(id => id !== departmentId);
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentIds: newDeptIds,
            departmentId: newDeptIds.length > 0 ? newDeptIds[0] : null, // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        // Add departmentId to new teamMembers
        const addedUsers = newTeamMembers.filter(id => !oldTeamMembers.includes(id));
        addedUsers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentDeptIds = Array.isArray(user?.departmentIds) ? user.departmentIds : (user?.departmentId ? [user.departmentId] : []);
          const newDeptIds = [...new Set([...currentDeptIds, departmentId])];
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentIds: newDeptIds,
            departmentId: newDeptIds[0], // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        if (removedUsers.length > 0 || addedUsers.length > 0) {
          await batch.commit();
          
          // Update local users state
          setUsers(prev => 
            prev.map(user => {
              if (removedUsers.includes(user.id)) {
                const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
                const newDeptIds = currentDeptIds.filter(id => id !== departmentId);
                return { 
                  ...user, 
                  departmentIds: newDeptIds, 
                  departmentId: newDeptIds.length > 0 ? newDeptIds[0] : null,
                  updatedAt: new Date() 
                };
              }
              if (addedUsers.includes(user.id)) {
                const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
                const newDeptIds = [...new Set([...currentDeptIds, departmentId])];
                return { 
                  ...user, 
                  departmentIds: newDeptIds, 
                  departmentId: newDeptIds[0],
                  updatedAt: new Date() 
                };
              }
              return user;
            })
          );
        }
      }
      
      // Also update local state immediately
      setDepartments(prev => 
        prev.map(dept => 
          dept.id === departmentId 
            ? { ...dept, ...updates, updatedAt: new Date() }
            : dept
        )
      );
      
    } catch (error) {
      console.error('Error updating department:', error);
      
      // Fallback to local state update only
      setDepartments(prev => 
        prev.map(dept => 
          dept.id === departmentId 
            ? { ...dept, ...updates, updatedAt: new Date() }
            : dept
        )
      );
    }
  };

  const deleteDepartment = async (departmentId) => {
    try {
      // Get current department to find teamMembers
      const currentDept = departments.find(d => d.id === departmentId);
      const teamMembers = currentDept?.teamMembers || [];
      
      // Remove departmentId from all team members (תמיכה במספר מחלקות)
      if (teamMembers.length > 0) {
        const batch = writeBatch(db);
        
        teamMembers.forEach(userId => {
          const user = users.find(u => u.id === userId);
          const currentDeptIds = Array.isArray(user?.departmentIds) ? user.departmentIds : (user?.departmentId ? [user.departmentId] : []);
          const newDeptIds = currentDeptIds.filter(id => id !== departmentId);
          
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentIds: newDeptIds,
            departmentId: newDeptIds.length > 0 ? newDeptIds[0] : null, // backward compatibility
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        // Update local users state
        setUsers(prev => 
          prev.map(user => {
            if (teamMembers.includes(user.id)) {
              const currentDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
              const newDeptIds = currentDeptIds.filter(id => id !== departmentId);
              return { 
                ...user, 
                departmentIds: newDeptIds, 
                departmentId: newDeptIds.length > 0 ? newDeptIds[0] : null,
                updatedAt: new Date() 
              };
            }
            return user;
          })
        );
      }
      
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.DEPARTMENTS, departmentId));
      
      // Also update local state immediately
      setDepartments(prev => prev.filter(dept => dept.id !== departmentId));
      
    } catch (error) {
      console.error('Error deleting department:', error);
      
      // Fallback to local state update only
      setDepartments(prev => prev.filter(dept => dept.id !== departmentId));
    }
  };

  // Auto-assign managers to management department
  const assignManagerToManagementDepartment = async (userId, userRole) => {
    try {
      console.log(`Attempting to assign user ${userId} with role ${userRole} to management department`);
      
      // Check if user is a manager type
      const isManager = ['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager'].includes(userRole);
      
      if (!isManager) {
        console.log(`User ${userId} is not a manager, skipping assignment`);
        return; // Only assign managers to management department
      }
      
      // Check if user is already in management department (תמיכה במספר מחלקות)
      const user = users.find(u => u.id === userId);
      if (user) {
        const userDeptIds = Array.isArray(user.departmentIds) ? user.departmentIds : (user.departmentId ? [user.departmentId] : []);
        const managementDept = departments.find(d => d.name === 'מחלקת הנהלה' || d.name === 'Management Department');
        if (managementDept && userDeptIds.includes(managementDept.id)) {
          console.log(`User ${userId} is already in management department, skipping assignment`);
          return;
        }
      }
      
      // Get or create management department
      let managementDeptId = await createManagementDepartment();
      
      if (!managementDeptId) {
        console.error('Could not create or find management department');
        return;
      }
      
      console.log(`Assigning user ${userId} to management department ${managementDeptId}`);
      
      // Assign user to management department (הוספה למערך)
      const currentDeptIds = Array.isArray(user?.departmentIds) ? user.departmentIds : (user?.departmentId ? [user.departmentId] : []);
      const newDeptIds = [...new Set([...currentDeptIds, managementDeptId])];
      await updateUser(userId, { 
        departmentIds: newDeptIds, 
        departmentId: newDeptIds[0] // backward compatibility
      });
      
      console.log(`User ${userId} successfully assigned to management department`);
    } catch (error) {
      console.error('Error assigning manager to management department:', error);
    }
  };

  // User operations
  const updateUser = async (userId, updates) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Force refresh users data from Firestore
      const usersQueryRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(usersQueryRef);
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
      
      // If updating current user, also update AuthContext
      if (userId === currentUser?.id) {
        updateCurrentUser(updates);
      }
      
    } catch (error) {
      // Fallback to local state update only
      setUsers(prev => 
        prev.map(user => 
          user.id === userId 
            ? { ...user, ...updates, updatedAt: new Date() }
            : user
        )
      );
      
      // If updating current user, also update AuthContext
      if (userId === currentUser?.id) {
        updateCurrentUser(updates);
      }
    }
  };

  // Comment operations
  const createComment = async (commentData) => {
    try {
      const newComment = {
        ...commentData,
        userId: effectiveUser.id,
        userName: effectiveUser.name || effectiveUser.email,
        createdAt: serverTimestamp(),
        isRead: false
      };
  
      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.COMMENTS), newComment);
      
      // Get the task
      const task = tasks.find(t => t.id === commentData.taskId);
      if (!task) return docRef.id;
      
      // Create notifications for all relevant users
      const notifyUsers = new Set();
      
      // Add task creator
      if (task.creatorId && task.creatorId !== effectiveUser.id) {
        notifyUsers.add(task.creatorId);
      }
      
      // Add assignee(s)
      if (task.assigneeId && task.assigneeId !== effectiveUser.id) {
        notifyUsers.add(task.assigneeId);
      }
      
      // Add all assignees if array exists
      if (Array.isArray(task.assigneeIds)) {
        task.assigneeIds.forEach(id => {
          if (id !== effectiveUser.id) {
            notifyUsers.add(id);
          }
        });
      }
      
      // Create notification for each relevant user
      for (const notifyUserId of notifyUsers) {
        await createNotification({
          userId: notifyUserId,
          type: 'comment',
          title: 'הערה חדשה במשימה',
          message: `${effectiveUser.name || effectiveUser.email} הוסיף/ה הערה במשימה "${task.title}"`,
          taskId: task.id,
          commentId: docRef.id,
          fromUserId: effectiveUser.id
        });
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  };

  const getTaskComments = (taskId) => {
    return comments.filter(comment => comment.taskId === taskId);
  };

  const getUnreadCommentsForUser = (userId) => {
    // Get all tasks where:
    // 1. User created the task OR
    // 2. User is assigned to the task
    const relevantTasks = tasks.filter(task => 
      task.creatorId === userId || 
      task.assigneeId === userId ||
      (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
    );
    
    const taskIds = relevantTasks.map(task => task.id);
    
    // Get all comments on those tasks that:
    // 1. Are not by the current user
    // 2. Are unread
    return comments.filter(comment => 
      taskIds.includes(comment.taskId) && 
      comment.userId !== userId &&
      !comment.isRead
    );
  };

  const markCommentAsRead = async (commentId) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.COMMENTS, commentId), {
        isRead: true,
        readAt: serverTimestamp()
      });
      
      // Update local state immediately for instant UI feedback
      setComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, isRead: true, readAt: new Date() }
            : comment
        )
      );
      
      console.log(`Comment ${commentId} marked as read`);
    } catch (error) {
      console.error('Error marking comment as read:', error);
    }
  };

  // Notification operations
  const createNotification = async (notificationData) => {
    try {
      const newNotification = {
        ...notificationData,
        createdAt: serverTimestamp(),
        isRead: false,
        actionRequired: notificationData.actionRequired ?? false
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), newNotification);

      return docRef.id;
    } catch (error) {
      console.error('Error creating notification in Firestore:', error);

      // Fallback to local state
      const localNotification = {
        id: `notification_${Date.now()}`,
        ...notificationData,
        createdAt: new Date(),
        isRead: false,
        actionRequired: notificationData.actionRequired ?? false
      };

      setNotifications(prev => [localNotification, ...prev]);
      return localNotification.id;
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), {
        isRead: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Fallback to local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, isRead: true, readAt: new Date() }
            : notification
        )
      );
    }
  };

  const updateNotification = async (notificationId, updates) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating notification:', error);
      // Fallback to local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, ...updates, updatedAt: new Date() }
            : notification
        )
      );
    }
  };

  // Weekly Plan operations
  const createWeeklyPlan = async (planData) => {
    try {
      const newPlan = {
        ...planData,
        userId: effectiveUser.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'weeklyPlans'), newPlan);
      return docRef.id;
    } catch (error) {
      console.error('Error creating weekly plan:', error);
      const localPlan = {
        id: `weekly_${Date.now()}`,
        ...planData,
        userId: effectiveUser.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setWeeklyPlans(prev => [localPlan, ...prev]);
      return localPlan.id;
    }
  };

  const updateWeeklyPlan = async (planId, updates) => {
    try {
      await updateDoc(doc(db, 'weeklyPlans', planId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating weekly plan:', error);
      setWeeklyPlans(prev => 
        prev.map(plan => 
          plan.id === planId 
            ? { ...plan, ...updates, updatedAt: new Date() }
            : plan
        )
      );
    }
  };

  const deleteWeeklyPlan = async (planId) => {
    try {
      await deleteDoc(doc(db, 'weeklyPlans', planId));
      setWeeklyPlans(prev => prev.filter(plan => plan.id !== planId));
    } catch (error) {
      console.error('Error deleting weekly plan:', error);
      setWeeklyPlans(prev => prev.filter(plan => plan.id !== planId));
    }
  };

  // Daily Plan operations
  const createDailyPlan = async (planData) => {
    try {
      const newPlan = {
        ...planData,
        userId: effectiveUser.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'dailyPlans'), newPlan);
      return docRef.id;
    } catch (error) {
      console.error('Error creating daily plan:', error);
      const localPlan = {
        id: `daily_${Date.now()}`,
        ...planData,
        userId: effectiveUser.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setDailyPlans(prev => [localPlan, ...prev]);
      return localPlan.id;
    }
  };

  const updateDailyPlan = async (planId, updates) => {
    try {
      await updateDoc(doc(db, 'dailyPlans', planId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating daily plan:', error);
      setDailyPlans(prev => 
        prev.map(plan => 
          plan.id === planId 
            ? { ...plan, ...updates, updatedAt: new Date() }
            : plan
        )
      );
    }
  };

  const deleteDailyPlan = async (planId) => {
    try {
      await deleteDoc(doc(db, 'dailyPlans', planId));
      setDailyPlans(prev => prev.filter(plan => plan.id !== planId));
    } catch (error) {
      console.error('Error deleting daily plan:', error);
      setDailyPlans(prev => prev.filter(plan => plan.id !== planId));
    }
  };

  // Meeting operations
  const createMeeting = async (meetingData) => {
    try {
      console.log('🔵 Creating meeting with data:', meetingData);
      console.log('🔵 Meeting status:', meetingData.status);
      console.log('🔵 Participant IDs:', meetingData.participantIds);
      console.log('🔵 Confirmed Participant IDs:', meetingData.confirmedParticipantIds);
      
      const newMeeting = {
        subject: meetingData.subject || '',
        purpose: meetingData.purpose || '',
        mainDepartmentId: meetingData.mainDepartmentId || '',
        participantIds: meetingData.participantIds || [],
        startDate: meetingData.startDate || new Date(),
        endDate: meetingData.endDate || null,
        location: meetingData.location || '',
        agendaItems: meetingData.agendaItems || [],
        decisions: meetingData.decisions || [],
        summary: meetingData.summary || '',
        newTasks: meetingData.newTasks || [],
        newProjects: meetingData.newProjects || [],
        linkedTaskIds: meetingData.linkedTaskIds || [],
        linkedProjectIds: meetingData.linkedProjectIds || [],
        facilitatorId: effectiveUser.id,
        facilitatorName: effectiveUser.name || effectiveUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: meetingData.status || 'in-progress', // scheduled, in-progress, completed
        confirmedParticipantIds: meetingData.confirmedParticipantIds || [] // For scheduled meetings
      };

      console.log('🟢 Prepared meeting object:', newMeeting);
      console.log('🟢 Confirmed IDs to save:', newMeeting.confirmedParticipantIds);
      const docRef = await addDoc(collection(db, COLLECTIONS.MEETINGS), newMeeting);
      console.log('✅ Meeting created successfully with ID:', docRef.id);
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating meeting in Firestore:', error);
      console.error('Error details:', error.message, error.code);
      
      // Fallback to local state
      const localMeeting = {
        id: `meeting_${Date.now()}`,
        subject: meetingData.subject || '',
        purpose: meetingData.purpose || '',
        mainDepartmentId: meetingData.mainDepartmentId || '',
        participantIds: meetingData.participantIds || [],
        startDate: meetingData.startDate || new Date(),
        endDate: meetingData.endDate || null,
        location: meetingData.location || '',
        agendaItems: meetingData.agendaItems || [],
        decisions: meetingData.decisions || [],
        summary: meetingData.summary || '',
        newTasks: meetingData.newTasks || [],
        newProjects: meetingData.newProjects || [],
        linkedTaskIds: meetingData.linkedTaskIds || [],
        linkedProjectIds: meetingData.linkedProjectIds || [],
        facilitatorId: effectiveUser.id,
        facilitatorName: effectiveUser.name || effectiveUser.email,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: meetingData.status || 'in-progress',
        confirmedParticipantIds: meetingData.confirmedParticipantIds || []
      };
      
      console.log('Using local fallback with ID:', localMeeting.id);
      setMeetings(prev => [localMeeting, ...prev]);
      return localMeeting.id;
    }
  };

  const updateMeeting = async (meetingId, updates) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.MEETINGS, meetingId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating meeting:', error);
      setMeetings(prev => 
        prev.map(meeting => 
          meeting.id === meetingId 
            ? { ...meeting, ...updates, updatedAt: new Date() }
            : meeting
        )
      );
    }
  };

  const deleteMeeting = async (meetingId) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.MEETINGS, meetingId));
      setMeetings(prev => prev.filter(meeting => meeting.id !== meetingId));
    } catch (error) {
      console.error('Error deleting meeting:', error);
      setMeetings(prev => prev.filter(meeting => meeting.id !== meetingId));
    }
  };

  const completeMeeting = async (meetingId, meetingData) => {
    try {
      console.log('Starting completeMeeting with:', { meetingId, meetingData });
      
      // Update meeting status to completed
      await updateDoc(doc(db, COLLECTIONS.MEETINGS, meetingId), {
        status: 'completed',
        endDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('Meeting status updated to completed');

      // Create tasks and projects that were defined in the meeting
      const createdTaskIds = [];
      const createdProjectIds = [];

      // Create new tasks
      if (meetingData.newTasks && meetingData.newTasks.length > 0) {
        for (const task of meetingData.newTasks) {
          // Skip tasks without title
          if (!task.title || task.title.trim() === '') {
            console.warn('Skipping task without title:', task);
            continue;
          }

          // Prepare task data with proper formatting
          const taskData = {
            title: task.title,
            description: task.description || '',
            assigneeIds: Array.isArray(task.assigneeIds) && task.assigneeIds.length > 0 
              ? task.assigneeIds 
              : [],
            deadline: task.deadline || null,
            priority: task.priority || 'medium',
            scope: task.scope || 'managerial',
            status: 'ממתין לעבודה',
            source: 'meeting',
            sourceId: meetingId
          };

          try {
            const taskId = await createTask(taskData);
            createdTaskIds.push(taskId);
          } catch (taskError) {
            console.error('Error creating task:', taskError, task);
            // Continue with other tasks even if one fails
          }
        }
      }

      // Create new projects
      if (meetingData.newProjects && meetingData.newProjects.length > 0) {
        for (const project of meetingData.newProjects) {
          // Skip projects without name
          if (!project.name || project.name.trim() === '') {
            console.warn('Skipping project without name:', project);
            continue;
          }

          // Prepare project data with proper formatting
          const projectData = {
            name: project.name,
            description: project.description || '',
            managerId: project.managerId || null,
            teamMembers: Array.isArray(project.teamMembers) && project.teamMembers.length > 0
              ? project.teamMembers
              : [],
            deadline: project.deadline || null,
            priority: project.priority || 'medium',
            status: 'במסלול',
            source: 'meeting',
            sourceId: meetingId
          };

          try {
            const projectId = await createProject(projectData);
            createdProjectIds.push(projectId);
          } catch (projectError) {
            console.error('Error creating project:', projectError, project);
            // Continue with other projects even if one fails
          }
        }
      }

      console.log('Created tasks:', createdTaskIds.length, 'Created projects:', createdProjectIds.length);

      // Update meeting with created task and project IDs
      try {
        await updateDoc(doc(db, COLLECTIONS.MEETINGS, meetingId), {
          createdTaskIds,
          createdProjectIds,
          linkedTaskIds: meetingData.linkedTaskIds || [],
          linkedProjectIds: meetingData.linkedProjectIds || []
        });
        console.log('Meeting updated with created IDs');
      } catch (updateError) {
        console.error('Error updating meeting with IDs:', updateError);
        // Continue even if this fails
      }

      // Send notifications to all assignees
      const notifyUsers = new Set();
      
      // Collect all assignees from new tasks
      if (meetingData.newTasks) {
        meetingData.newTasks.forEach(task => {
          if (task.assigneeId) notifyUsers.add(task.assigneeId);
          if (Array.isArray(task.assigneeIds)) {
            task.assigneeIds.forEach(id => notifyUsers.add(id));
          }
        });
      }

      // Collect all team members from new projects
      if (meetingData.newProjects) {
        meetingData.newProjects.forEach(project => {
          if (project.managerId) notifyUsers.add(project.managerId);
          if (Array.isArray(project.teamMembers)) {
            project.teamMembers.forEach(id => notifyUsers.add(id));
          }
        });
      }

      console.log('Sending notifications to:', notifyUsers.size, 'users');

      // Send notifications (don't fail if notifications fail)
      try {
        for (const userId of notifyUsers) {
          if (userId !== effectiveUser.id) {
            try {
              await createNotification({
                userId,
                type: 'meeting',
                title: 'משימות חדשות מישיבת הנהלה',
                message: `הוקצו לך משימות ופרויקטים חדשים מישיבה: ${meetingData.subject}`,
                meetingId
              });
            } catch (notifError) {
              console.error('Error sending notification to user:', userId, notifError);
              // Continue with other notifications
            }
          }
        }
        console.log('Notifications sent successfully');
      } catch (notifError) {
        console.error('Error in notification process:', notifError);
        // Don't fail the whole process if notifications fail
      }

      console.log('Meeting completed successfully!');
      return { createdTaskIds, createdProjectIds };
    } catch (error) {
      console.error('Error completing meeting:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  };

  // Check and send meeting reminders for today
  const checkAndSendMeetingReminders = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1); // Start of tomorrow

      // Find all scheduled meetings for today
      const todaysMeetings = meetings.filter(meeting => {
        if (meeting.status !== 'scheduled') return false;
        if (!meeting.startDate) return false;

        const meetingDate = meeting.startDate.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
        return meetingDate >= today && meetingDate < tomorrow;
      });

      console.log(`Found ${todaysMeetings.length} meetings today`);

      // Send reminders to confirmed participants
      for (const meeting of todaysMeetings) {
        if (!meeting.confirmedParticipantIds || meeting.confirmedParticipantIds.length === 0) {
          continue;
        }

        const meetingDate = meeting.startDate.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
        const meetingTime = meetingDate.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });

        // Send reminder to each confirmed participant
        for (const participantId of meeting.confirmedParticipantIds) {
          try {
            // Check if reminder already sent today
            const existingReminder = notifications.find(n =>
              n.userId === participantId &&
              n.type === 'meeting_reminder' &&
              n.meetingId === meeting.id &&
              n.createdAt &&
              new Date(n.createdAt).toDateString() === today.toDateString()
            );

            if (existingReminder) {
              console.log(`Reminder already sent to ${participantId} for meeting ${meeting.id}`);
              continue;
            }

            await createNotification({
              userId: participantId,
              type: 'meeting_reminder',
              title: 'תזכורת: ישיבה היום',
              message: `תזכורת לישיבה "${meeting.subject}" היום בשעה ${meetingTime}${meeting.location ? ` ב${meeting.location}` : ''}`,
              meetingId: meeting.id,
              priority: 'high'
            });

            console.log(`Sent reminder to ${participantId} for meeting ${meeting.id}`);
          } catch (error) {
            console.error(`Error sending reminder to ${participantId}:`, error);
            // Continue with other participants
          }
        }
      }
    } catch (error) {
      console.error('Error in checkAndSendMeetingReminders:', error);
    }
  };

  // Run meeting reminder check once when component mounts and then every hour
  useEffect(() => {
    if (!effectiveUser?.id || meetings.length === 0) return;

    // Check immediately on mount
    checkAndSendMeetingReminders();

    // Then check every hour
    const intervalId = setInterval(checkAndSendMeetingReminders, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [effectiveUser?.id, meetings]);

  const value = {
    // Data
    departments,
    teams,
    projects,
    tasks,
    templates,
    notifications,
    users,
    comments,
    suggestions,
    weeklyPlans,
    dailyPlans,
    meetings,
    
    // Task operations
    createTask,
    updateTask,
    acknowledgeTask,
    approveTask,
    deleteTask,
    restoreTask,
    permanentDeleteTask,
    
    // Project operations
    createProject,
    updateProject,
    deleteProject,

    // Team operations
    createTeam,
    updateTeam,
    deleteTeam,

    // Department operations
    createDepartment,
    createManagementDepartment,
    updateDepartment,
    deleteDepartment,
    
    // User operations
    updateUser,
    assignManagerToManagementDepartment,
    
    // Comment operations
    createComment,
    getTaskComments,
    getUnreadCommentsForUser,
    markCommentAsRead,
    
    // Notification operations
    createNotification,
    markNotificationAsRead,
    updateNotification,

    // Suggestion operations
    createSuggestion,
    updateSuggestion,
    deleteSuggestion,
    
    // Weekly/Daily Plan operations
    createWeeklyPlan,
    updateWeeklyPlan,
    deleteWeeklyPlan,
    createDailyPlan,
    updateDailyPlan,
    deleteDailyPlan,
    
    // Meeting operations
    createMeeting,
    updateMeeting,
    deleteMeeting,
    completeMeeting,
    
    // Impersonation operations
    impersonatedUser,
    setImpersonatedUser,
    effectiveUser,
    currentUser,
    
    // Utilities
    loading,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};